import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

type Theme = "dark" | "light";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  siteThemeName: string | null;
  siteThemeSlug: string | null;
  bannerText: string | null;
  bannerEmoji: string | null;
  bannerCtaText: string | null;
  bannerBgColor: string | null;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// CSS variable keys that the site_themes table can override
const THEME_CSS_KEYS = [
  "primary", "primary-foreground", "secondary", "secondary-foreground",
  "accent", "accent-foreground", "destructive", "destructive-foreground",
  "success", "success-foreground", "warning", "warning-foreground",
];

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window !== "undefined") {
      // The brand look (Pearl and navy) is light; dark stays one click away in the toggle
      const stored = localStorage.getItem("theme") as Theme;
      if (stored === "dark" || stored === "light") return stored;
    }
    return "light";
  });
  const [siteThemeName, setSiteThemeName] = useState<string | null>(null);
  const [siteThemeSlug, setSiteThemeSlug] = useState<string | null>(null);
  const [bannerText, setBannerText] = useState<string | null>(null);
  const [bannerEmoji, setBannerEmoji] = useState<string | null>(null);
  const [bannerCtaText, setBannerCtaText] = useState<string | null>(null);
  const [bannerBgColor, setBannerBgColor] = useState<string | null>(null);

  // Fetch and apply active site theme from DB
  useEffect(() => {
    const applySiteTheme = async () => {
      try {
        const { data } = await supabase
          .from("site_themes")
          .select("name, slug, css_variables, banner_text, banner_emoji, banner_cta_text, banner_bg_color")
          .eq("is_active", true)
          .maybeSingle();

        if (data?.css_variables && typeof data.css_variables === "object") {
          const vars = data.css_variables as Record<string, string>;
          const root = document.documentElement;
          // The default look is the stylesheet's own light/dark palette; only seasonal themes
          // (Diwali, Christmas, ...) override colours
          if (data.slug !== "default") {
            THEME_CSS_KEYS.forEach((key) => {
              if (vars[key]) {
                root.style.setProperty(`--${key}`, vars[key]);
              }
            });
          }
          setSiteThemeName(data.name);
          setSiteThemeSlug(data.slug);
        }

        setBannerText(data?.banner_text ?? null);
        setBannerEmoji(data?.banner_emoji ?? null);
        setBannerCtaText(data?.banner_cta_text ?? null);
        setBannerBgColor(data?.banner_bg_color ?? null);
      } catch (err) {
        console.error("Failed to load site theme:", err);
      }
    };
    applySiteTheme();
  }, []);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    localStorage.setItem("theme", theme);

    // Brand favicon (single asset for light & dark)
    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (favicon) {
      favicon.href = "/favicon.png";
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, siteThemeName, siteThemeSlug, bannerText, bannerEmoji, bannerCtaText, bannerBgColor }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
